import {
  Directive,
  HostListener,
  inject,
  input,
  Input,
  model,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { ModalsService } from 'src/app/services/Modals/modals.service';

@Directive({
  selector: '[image-dropbox]',
  standalone: true,
})
export class ImageUploaderDirective {
  modals = inject(ModalsService);

  filesDragging = model<boolean>();
  multiple = input<boolean>(true);
  supportedFormats = input.required<string>();
  maxFileSize = input<number>();
  dropFiles = output<ImageFile[]>();
  allowed_ext: string[] = []

  fileInput: HTMLInputElement;
  constructor(private modal: ModalsService) {
    this.fileInput = document.createElement('input');
  }

  ngOnInit() {
    this.fileInput.type = 'file';
    this.fileInput.accept = this.supportedFormats() || 'image/*';
    this.fileInput.addEventListener('change', (event) =>
      this.onFileSelect(event),
    );
    this.allowed_ext = this.supportedFormats()
      .split(',')
      .map((i) => i.trim().replace('.', ''));
    this.fileInput.multiple = this.multiple();
  }

  @HostListener('dragover', ['$event']) public dragOver(event: DragEvent) {
    this.preventDefaults(event);
    this.filesDragging.set(true);
  }

  @HostListener('dragleave', ['$event']) public dragLeave(event: DragEvent) {
    this.preventDefaults(event);
    this.filesDragging.set(false);
  }

  @HostListener('drop', ['$event']) public onDrop(event: DragEvent) {
    this.filesDragging.set(false);
    this.preventDefaults(event);

    if (event.dataTransfer && event.dataTransfer.files) {
      let fileList = event.dataTransfer.files;
      let files: ImageFile[] = [];

      // ensure only one file can be dropped if multiple is false
      if (!this.multiple && fileList.length > 1) {
        this.modal
          .getInstance()
          .showInfo(
            'Warning',
            `Only one image can be dropped. Supported format: ${this.supportedFormats}. Max file size: ${this.maxFileSize} MB`,
            'Close',
          );
        return;
      }
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const url = URL.createObjectURL(file);
        files.push({ file, url });
      }
      if (files.length > 0) {
        this.dropFiles.emit(files);
      }
    }
  }

  @HostListener('click')
  public onClick() {
    this.fileInput.click();
  }

  private onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input && input.files) {
      const fileList = input.files;
      let files: ImageFile[] = [];
      let with_invalid_types = false;
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        if (!this.supportedFormats().includes(this.getExtension(file).toLowerCase())) {
          with_invalid_types = true;
          continue;
        }
        const url = URL.createObjectURL(file);
        files.push({ file, url });
      }
      if (with_invalid_types) {
        this.modals.getInstance()?.showInfo('Error', 'Invalid file type', 'OK');
      }
      if (files.length > 0) {
        this.dropFiles.emit(files);
      }
    }
  }

  preventDefaults(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  getExtension(file: File) {
    return file.name.split('.').pop() ?? '--';
  }
}
export interface ImageFile {
  file: File;
  url: SafeUrl;
}
